import { isInMsTeamsAuthWrapper, useAuth } from "@healthos/auth/react";
import { initialsFrom } from "@healthos/shared";
import { cn } from "@healthos/ui";
import { BellSimple, Bug, ChatText, GearSix, SignOut, UserCircle } from "@phosphor-icons/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { communityQueryKeys, fetchCommunity } from "../api/community";
import { useMemberName } from "../hooks/use-member-name";
import { useUnreadNotificationCount } from "../hooks/use-notifications";
import { Avatar } from "../pages/community/shared";
import { ReportBugConfirmModal } from "./report-bug-confirm-modal";

/**
 * The member's personal entry point in the companion's top navigation.
 * Community owns the avatar asset, so this reads the same cached response as
 * the profile and community screens instead of introducing another identity
 * source.
 */
// rounded-md so a 40px row reads as a row, matching the sidebar nav.
const MENU_ITEM =
  "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors";

export function ProfileNavTrigger({ onFeedbackClick }: { onFeedbackClick?: () => void }) {
  const { state, signOut } = useAuth();
  const [bugLinkConfirmOpen, setBugLinkConfirmOpen] = useState(false);
  const { data: communityData } = useQuery({
    queryKey: communityQueryKeys.feed,
    queryFn: () => fetchCommunity(),
    staleTime: 30_000,
  });

  const memberName = useMemberName();
  const displayName = memberName?.fullName ?? "Your profile";
  const avatarUrl = communityData?.preferences.avatarUrl ?? null;
  const profileLabel = memberName?.firstName ?? "Profile";
  const showSignOut = state.status === "signedIn" && !isInMsTeamsAuthWrapper();
  const { count: unreadNotificationCount } = useUnreadNotificationCount({
    enabled: state.status === "signedIn",
  });
  const notificationBadge = formatNotificationBadge(unreadNotificationCount);
  // 2.5.3: the name has to start with the name on screen, or "click <first name>" misses.
  const accountLabel = unreadNotificationCount
    ? `${profileLabel}, account menu, ${unreadNotificationCount} unread notifications`
    : `${profileLabel}, account menu`;

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={accountLabel}
          title="Account"
          className="group relative inline-flex h-11 shrink-0 items-center gap-2.5 rounded-xl border border-border-strong/55 pl-3 pr-1 transition-[border-color,transform] duration-180 ease-out hover:border-border-strong/65 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 motion-reduce:transform-none"
          style={{
            backgroundImage:
              "linear-gradient(160deg, color-mix(in srgb, var(--color-teal-100) 42%, var(--color-surface-elevated)) 0%, var(--color-surface-elevated) 50%, color-mix(in srgb, var(--color-teal-100) 34%, var(--color-surface-elevated)) 100%)",
          }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl"
          >
            <span className="absolute inset-y-0 left-0 w-2/3 -translate-x-[160%] skew-x-[-18deg] bg-linear-to-r from-transparent via-surface-elevated/65 to-transparent [transition:transform_220ms_var(--ease-out-expo)] group-hover:translate-x-[340%] motion-reduce:transition-none motion-reduce:group-hover:-translate-x-[160%]" />
          </span>
          <span className="relative z-10 hidden min-w-0 whitespace-nowrap text-sm font-medium tracking-[-0.005em] text-ink-primary sm:block">
            {profileLabel}
          </span>
          <span className="relative z-10 block shrink-0">
            <Avatar
              author={{
                displayName,
                initials: initialsFrom(displayName, "?"),
                avatarColor: null,
                avatarUrl,
              }}
              size="smPlus"
              className="!h-9 !w-9 transition-transform duration-200 group-hover:scale-[1.04] motion-reduce:transition-none"
            />
            {unreadNotificationCount ? (
              <NotificationBadge className="absolute -right-1 -top-1" label={notificationBadge} />
            ) : null}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={10}
          className="z-[60] min-w-52 overflow-hidden rounded-lg border border-border-strong/45 bg-surface-elevated p-1.5 shadow-[0_2px_6px_color-mix(in_srgb,var(--color-scrim)_7%,transparent),0_18px_40px_color-mix(in_srgb,var(--color-scrim)_12%,transparent)] outline-none"
        >
          <DropdownMenu.Label className="px-3 pb-2 pt-1 font-sans text-xs font-medium text-ink-quaternary">
            Account
          </DropdownMenu.Label>
          <DropdownMenu.Item asChild>
            <NavLink
              to="/profile"
              viewTransition
              className={cn(
                MENU_ITEM,
                "text-ink-primary hover:bg-surface-track/70 focus:bg-surface-track/70",
              )}
            >
              <UserCircle
                size={18}
                weight="regular"
                className="text-ink-brand"
                aria-hidden="true"
              />
              Profile
            </NavLink>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <NavLink
              to="/settings"
              viewTransition
              className={cn(
                MENU_ITEM,
                "text-ink-primary hover:bg-surface-track/70 focus:bg-surface-track/70",
              )}
            >
              <GearSix size={18} weight="regular" className="text-ink-brand" aria-hidden="true" />
              Settings
            </NavLink>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <NavLink
              to="/notifications"
              viewTransition
              className={cn(
                MENU_ITEM,
                "text-ink-primary hover:bg-surface-track/70 focus:bg-surface-track/70",
              )}
            >
              <BellSimple
                size={18}
                weight="regular"
                className="text-ink-brand"
                aria-hidden="true"
              />
              Notifications
              {unreadNotificationCount ? (
                <NotificationBadge className="ml-auto" label={notificationBadge} />
              ) : null}
            </NavLink>
          </DropdownMenu.Item>
          {onFeedbackClick ? (
            <DropdownMenu.Item
              onSelect={onFeedbackClick}
              className={cn(
                MENU_ITEM,
                "text-ink-primary hover:bg-surface-track/70 focus:bg-surface-track/70",
              )}
            >
              <ChatText size={18} weight="regular" className="text-ink-brand" aria-hidden="true" />
              Feedback
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Item
            onSelect={() => setBugLinkConfirmOpen(true)}
            className={cn(
              MENU_ITEM,
              "text-ink-primary hover:bg-surface-track/70 focus:bg-surface-track/70",
            )}
          >
            <Bug size={18} weight="regular" className="text-ink-brand" aria-hidden="true" />
            Report a bug
          </DropdownMenu.Item>
          {showSignOut ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-mist-dark/70" />
              <DropdownMenu.Item
                onSelect={() => signOut()}
                className={cn(
                  MENU_ITEM,
                  "text-ink-secondary hover:bg-rose-50 hover:text-rose-700 focus:bg-rose-50 focus:text-rose-700",
                )}
              >
                <SignOut size={18} weight="regular" aria-hidden="true" />
                Sign out
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
      <ReportBugConfirmModal open={bugLinkConfirmOpen} onOpenChange={setBugLinkConfirmOpen} />
    </DropdownMenu.Root>
  );
}

function NotificationBadge({ className, label }: { className: string; label: string }) {
  return (
    <span
      aria-hidden="true"
      className={`grid h-4 min-w-4 place-items-center rounded-full border-2 border-surface-elevated bg-rose-600 px-0.5 font-mono text-2xs font-bold leading-none text-ink-on-media ${className}`}
    >
      {label}
    </span>
  );
}

function formatNotificationBadge(count: number): string {
  return count > 99 ? "99+" : String(count);
}
